/**
 * The people we deal with at a supplier — list, add, edit, retire — as a
 * detail-page section. ONE component for hotels, airlines and car companies.
 *
 * Generalised from HotelContacts (2026-10-08) when Vendor Relations' airline and
 * car vendors gained contacts. The two tables have the same rules on purpose —
 * name required, at most one active primary, rows retired rather than deleted,
 * an edit PATCHes only what changed — so a second copy of this component would
 * have been a second set of those rules waiting to drift. What differs is passed
 * in as a `ContactsAdapter`: the endpoint, the parent key, the departed status
 * ('left_property' vs 'left_company'), whether a read-only feed row can appear,
 * and the copy that names the parent ("this property" vs "this vendor").
 *
 * WHY THE ROWS SAY WHERE THEY CAME FROM (hotels only). An `owned` contact is a
 * record we keep and can correct. The hotel `feed` row is one contact mirrored
 * onto hotel_master from the Domo contracts feed, and it is NOT editable —
 * hotel_master is a delete-all-and-reinsert mirror, so an edit written there
 * would survive until the next sync and then vanish. Vendor contacts have no feed
 * at all, so the Source column is not shown for them.
 *
 * Fetches its own data and owns THREE states — loading, failed, loaded — and says
 * which it is in: "no contacts" and "the request failed" render identically if you
 * let them, and the first is a finding while the second is a bug.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { contactHref } from '@/features/crm/lib/contact-href';
import { useMe } from '@/features/crm/auth/use-capabilities';

/** The departed status each table uses. */
export type DepartedStatus = 'left_property' | 'left_company';

/**
 * One contact as GET /api/btp/hotel-contacts or /api/btp/vendor-contacts returns
 * it. Mirrors `HotelContactView` in customer-management-api/src/lib/hsp/
 * hotel-contact (the vendor route sends the same shape).
 *
 * `id` is null for the hotel feed row, because it is not a record — there is
 * nothing to PATCH. That is what `editable` encodes, and the two travel together on
 * purpose so a caller cannot read one without the other.
 */
export interface SupplierContactRow {
  id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  isPrimary: boolean;
  status: 'active' | DepartedStatus;
  notes: string | null;
  provenance: 'owned' | 'feed';
  editable: boolean;
}

interface ContactsResponse {
  contacts: SupplierContactRow[];
}

/**
 * Everything that differs between a hotel's contacts and a vendor's. The copy is
 * here rather than derived from a noun, because the sentences are not the same
 * shape — a hotel's empty state has to mention the contract feed, a vendor's has
 * nothing to mention.
 */
export interface ContactsAdapter {
  /** GET/POST/PATCH endpoint, e.g. '/api/btp/hotel-contacts'. */
  endpoint: string;
  /** The parent key: query param on GET, body field on POST. */
  parentParam: 'hotel_canonical_id' | 'vendor_uid';
  parentId: string;
  departedStatus: DepartedStatus;
  /** Whether a read-only feed row can appear — shows the Source column. */
  hasFeed: boolean;
  copy: {
    /** "this property" / "this vendor" */
    parent: string;
    /** Sentence after the load-error lead, telling the reader how to retry. */
    retry: string;
    empty: string;
    allRetired: string;
    addButton: string;
    primaryLabel: string;
    departedLabel: string;
    departedPrimaryHint: string;
  };
}

/** Blank fields render as an em-dash rather than collapsing the row's shape. */
function orDash(v: string | null): string {
  return v && v.trim() !== '' ? v : '—';
}

export interface SupplierContactsProps {
  adapter: ContactsAdapter;
  testId: string;
  /**
   * The first page of contacts, when the parent already fetched it alongside its
   * own record. The component then renders in the SAME commit as the parent
   * instead of a beat later — which matters to the click-path crawl, whose plan
   * is taken when the page lands: a section whose buttons arrive 50 ms after the
   * header reads as a screen that "renders non-deterministically". Refetches
   * after a write are still this component's own.
   */
  initialContacts?: SupplierContactRow[];
}

export function SupplierContacts({
  adapter,
  testId,
  initialContacts,
}: SupplierContactsProps) {
  const { can } = useMe();
  const canWrite = can('write:supplier');
  const { endpoint, parentParam, parentId, departedStatus, copy } = adapter;

  const [contacts, setContacts] = useState<SupplierContactRow[] | null>(
    initialContacts ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(initialContacts === undefined);
  /** Seeded by the parent: the first load is already done. */
  const seeded = useRef(initialContacts !== undefined);
  /** Which row's edit form is open, by id. Null = none; 'new' = the add form. */
  const [editing, setEditing] = useState<string | null>(null);
  /** Which row is mid-retire, so its button can say so and not double-fire. */
  const [retiring, setRetiring] = useState<string | null>(null);
  /** Whether the removed-contacts list is expanded. */
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<ContactsResponse>(
      `${endpoint}?${parentParam}=${encodeURIComponent(parentId)}`,
    )
      .then((d) => {
        if (!cancelled) setContacts(d.contacts);
      })
      .catch((e) => {
        if (!cancelled) {
          // Kept as an error rather than falling back to an empty list. An empty
          // list is a claim about the SUPPLIER ("we know nobody here"); a failed
          // request is a claim about US.
          setError(e instanceof ApiError ? e.message : String(e));
          setContacts(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint, parentParam, parentId]);

  useEffect(() => {
    if (seeded.current) {
      seeded.current = false;
      return;
    }
    return load();
  }, [load]);

  const afterSave = () => {
    setEditing(null);
    load();
  };

  /**
   * Retire or restore one contact — one PATCH, one field.
   *
   * NOT a DELETE, and the request is the proof: `status` moves between 'active'
   * and the departed status. The row leaves the list either way; the record, and
   * every contact logged against it, survives.
   *
   * Confirmed before the write when retiring, because from where the user
   * stands this IS a delete — the name disappears from the list they were
   * looking at. Restoring needs no confirmation: it only puts something back.
   */
  const setStatus = async (
    id: string,
    name: string,
    status: 'active' | DepartedStatus,
  ) => {
    if (retiring) return;
    if (
      status === departedStatus &&
      !window.confirm(
        `Remove ${name} from ${copy.parent}'s contacts?\n\n` +
          'Their record and any contact history logged against them are kept — ' +
          'they just stop appearing in the contact list and pickers.',
      )
    ) {
      return;
    }
    setRetiring(id);
    setError(null);
    try {
      await apiFetch(endpoint, {
        method: 'PATCH',
        body: JSON.stringify({ id, status }),
      });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRetiring(null);
    }
  };

  const retire = (id: string, name: string) =>
    setStatus(id, name, departedStatus);
  const restore = (id: string, name: string) => setStatus(id, name, 'active');

  if (loading && contacts === null) {
    return (
      <p className="detail-note" data-testid={`${testId}-loading`}>
        <span className="muted">Loading contacts…</span>
      </p>
    );
  }

  if (error) {
    return (
      <p className="detail-note" data-testid={`${testId}-error`}>
        <span className="muted">
          Contacts aren&rsquo;t available right now — this is a loading or
          connection problem, not an empty list. {copy.retry}
        </span>
      </p>
    );
  }

  const all = contacts ?? [];
  /*
   * RETIRED CONTACTS COME OUT OF THE LIST — Jeff, 2026-09-03, answering
   * Stacie's third request for a delete: "You can retire it; remove it from the
   * UI but keep it in the data. It can still show in the contact history
   * tables."
   *
   * So this is not a delete and not the amber badge it used to be. The row
   * leaves the list, the record stays, and every logged contact recorded
   * against that person still resolves to their name — which is the whole
   * reason `hotel_contact.status` exists rather than a DELETE.
   *
   * They stay REACHABLE behind a count, not gone. Someone will retire the wrong
   * person, and a retirement you cannot see is one you cannot undo.
   */
  const rows = all.filter((c) => c.status === 'active');
  const retired = all.filter((c) => c.status !== 'active');
  const hasOwned = all.some((c) => c.provenance === 'owned');

  return (
    <div data-testid={testId}>
      {rows.length === 0 && retired.length > 0 && (
        <p className="detail-note" data-testid={`${testId}-all-retired`}>
          <span className="muted">{copy.allRetired}</span>
        </p>
      )}

      {rows.length === 0 && retired.length === 0 && (
        <p className="detail-note" data-testid={`${testId}-empty`}>
          <span className="muted">{copy.empty}</span>
        </p>
      )}

      {rows.length > 0 && (
        <table className="detail-table" data-testid={`${testId}-table`}>
          <thead>
            <tr>
              <th>Contact</th>
              <th>Role</th>
              <th>Email</th>
              <th>Phone</th>
              {adapter.hasFeed && <th>Source</th>}
              {canWrite && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                data-testid={`${testId}-row-${c.id ?? 'feed'}`}
                key={c.id ?? 'feed'}
              >
                <td style={{ fontWeight: 600 }}>
                  {c.name}
                  {c.isPrimary && (
                    <>
                      {' '}
                      <span className="badge badge-blue badge-no-dot">
                        Primary
                      </span>
                    </>
                  )}
                  {/* No "Left the property" badge here any more: a retired
                      contact is filtered OUT of this table and listed under the
                      removed disclosure instead. The badge lives there. */}
                </td>
                <td>{orDash(c.role)}</td>
                <td>
                  {c.email ? (
                    <a href={contactHref('mailto', c.email)}>{c.email}</a>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {c.phone ? (
                    <a href={contactHref('tel', c.phone)}>{c.phone}</a>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                {adapter.hasFeed && (
                  <td>
                    {c.provenance === 'owned' ? (
                      <span className="muted">Ours</span>
                    ) : (
                      <span
                        className="badge badge-neutral badge-no-dot"
                        data-testid={`${testId}-feed-badge`}
                        title="From the Domo contract feed. Not editable — the next sync overwrites it."
                      >
                        Contract feed
                      </span>
                    )}
                  </td>
                )}
                {canWrite && (
                  <td className="right">
                    {c.editable && c.id ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          gap: 6,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <button
                          className="btn btn-sm"
                          data-testid={`${testId}-edit-${c.id}`}
                          onClick={() =>
                            setEditing(editing === c.id ? null : c.id)
                          }
                          type="button"
                        >
                          {editing === c.id ? 'Cancel' : 'Edit'}
                        </button>
                        {/* "Remove", because that is the word for what it does
                            to the list. It retires the record — see the
                            partition above — so the label promises exactly what
                            happens and no more. The old route to this was a
                            "Left the property" checkbox inside the edit form,
                            which is three clicks and a piece of vocabulary
                            nobody asked for. */}
                        <button
                          className="btn btn-sm"
                          data-testid={`${testId}-remove-${c.id}`}
                          disabled={retiring === c.id}
                          onClick={() => retire(c.id!, c.name)}
                          title={`Remove from ${copy.parent}'s contacts. The record is kept so past contact history still resolves to their name.`}
                          type="button"
                        >
                          {retiring === c.id ? 'Removing…' : 'Remove'}
                        </button>
                      </span>
                    ) : (
                      <span className="muted" style={{ fontSize: 11 }}>
                        Read-only
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The feed row is a stand-in, so say what to do about it. Without this the
          reader sees a contact, tries to correct it, finds it read-only, and has no
          idea the answer is "add one of ours". */}
      {adapter.hasFeed && !hasOwned && rows.length > 0 && (
        <p className="detail-note" data-testid={`${testId}-feed-note`}>
          <span className="muted">
            This contact comes from the contract feed and cannot be edited — the
            next sync would overwrite the change. Add a contact of our own and
            it becomes the one we keep.
          </span>
        </p>
      )}

      {canWrite ? (
        <>
          {editing === 'new' ? (
            <ContactForm
              adapter={adapter}
              onCancel={() => setEditing(null)}
              onSaved={afterSave}
              testId={`${testId}-add`}
            />
          ) : (
            <button
              className="btn btn-sm"
              data-testid={`${testId}-add-button`}
              onClick={() => setEditing('new')}
              style={{ marginTop: 10 }}
              type="button"
            >
              {/* "Add a contact" was "a little vague" — Stacie, 2026-09-03, on
                  a screen where "contact" also means a logged call. The label
                  names the noun: a person at the supplier, not an interaction. */}
              {copy.addButton}
            </button>
          )}

          {rows.map((c) =>
            c.id && editing === c.id ? (
              <ContactForm
                adapter={adapter}
                contact={c}
                key={`edit-${c.id}`}
                onCancel={() => setEditing(null)}
                onSaved={afterSave}
                testId={`${testId}-edit-form`}
              />
            ) : null,
          )}

          {/* REMOVED CONTACTS, behind a count.
              Collapsed because the list is meant to be who to call; expandable
              because a retirement has to be undoable by the person who made it,
              and because "why does the history mention someone who isn't here"
              is answered by looking. */}
          {retired.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button
                aria-expanded={showRetired}
                className="btn btn-sm"
                data-testid={`${testId}-retired-toggle`}
                onClick={() => setShowRetired((v) => !v)}
                type="button"
              >
                {showRetired ? 'Hide' : 'Show'} removed ({retired.length})
              </button>

              {showRetired && (
                <table
                  className="detail-table"
                  data-testid={`${testId}-retired-table`}
                  style={{ marginTop: 8 }}
                >
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Role</th>
                      <th>Email</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {retired.map((c) => (
                      <tr
                        data-testid={`${testId}-retired-row-${c.id}`}
                        key={`retired-${c.id}`}
                      >
                        <td>
                          {c.name}{' '}
                          <span className="badge badge-amber badge-no-dot">
                            No longer there
                          </span>
                        </td>
                        <td>{orDash(c.role)}</td>
                        <td>{orDash(c.email)}</td>
                        <td className="right">
                          {c.id && (
                            <button
                              className="btn btn-sm"
                              data-testid={`${testId}-restore-${c.id}`}
                              disabled={retiring === c.id}
                              onClick={() => restore(c.id!, c.name)}
                              type="button"
                            >
                              {retiring === c.id ? 'Restoring…' : 'Restore'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      ) : (
        // Says why rather than rendering nothing. A missing button reads as a bug,
        // or as a feature you have not found; this reads as a permission. The
        // server gate is the real one.
        <p className="detail-note" data-testid={`${testId}-readonly`}>
          <span className="muted">
            You have read-only access to supplier records, so editing contacts
            isn&rsquo;t available on this account.
          </span>
        </p>
      )}
    </div>
  );
}

/* ── The add / edit form ──────────────────────────────────────────────────── */

/**
 * One form for both create and edit, because the fields are identical and a second
 * copy would drift the moment either grew one.
 *
 * The difference is the REQUEST, and it is the interesting part. A create POSTs
 * everything. An edit PATCHes only what changed — which is what makes the server's
 * absent-versus-empty distinction usable: sending an unchanged field would be
 * harmless, but sending every field means two people editing different fields on the
 * same contact overwrite each other. Sending only what you touched means they don't.
 */
function ContactForm({
  adapter,
  contact,
  onCancel,
  onSaved,
  testId,
}: {
  adapter: ContactsAdapter;
  /** Absent = create. Present = edit that contact. */
  contact?: SupplierContactRow;
  onCancel: () => void;
  onSaved: () => void;
  testId: string;
}) {
  const { endpoint, parentParam, parentId, departedStatus, copy } = adapter;
  const [name, setName] = useState(contact?.name ?? '');
  const [role, setRole] = useState(contact?.role ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false);
  const [left, setLeft] = useState(
    contact != null && contact.status !== 'active',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editing = contact != null && contact.id != null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard the handler as well as the button: a form submits on Enter from any
    // field, which never touches the disabled button.
    if (saving) return;

    // Trimmed here as well as on the server, so the button's own disabled check and
    // what gets sent agree about what "empty" means.
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setError('A contact needs a name.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editing) {
        // ONLY the changed keys. An absent key means "leave it alone" server-side,
        // so this is what keeps two people editing different fields from
        // overwriting each other.
        const patch: Record<string, unknown> = { id: contact!.id };
        if (trimmedName !== contact!.name) patch.name = trimmedName;
        if (role.trim() !== (contact!.role ?? '')) patch.role = role.trim();
        if (email.trim() !== (contact!.email ?? '')) patch.email = email.trim();
        if (phone.trim() !== (contact!.phone ?? '')) patch.phone = phone.trim();
        if (isPrimary !== contact!.isPrimary) patch.is_primary = isPrimary;
        const nextStatus = left ? departedStatus : 'active';
        if (nextStatus !== contact!.status) patch.status = nextStatus;

        // Nothing actually changed. Sending it would bump updated_at and write an
        // audit row recording an edit that edited nothing, and the server rejects an
        // empty patch anyway — so close the form rather than show that as an error.
        if (Object.keys(patch).length === 1) {
          onCancel();
          return;
        }
        await apiFetch(endpoint, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
      } else {
        await apiFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify({
            [parentParam]: parentId,
            name: trimmedName,
            role: role.trim(),
            email: email.trim(),
            phone: phone.trim(),
            is_primary: isPrimary,
          }),
        });
      }
      onSaved();
    } catch (err) {
      // The server's message, verbatim — it names the field and the rule ("'x' does
      // not look like an email address"), which is more use than anything this
      // component could say generically.
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid={testId}
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 8,
        marginTop: 10,
        padding: 12,
        border: '1px solid var(--border-light)',
        borderRadius: 4,
      }}
    >
      <div className="detail-grid" style={{ gap: '8px 14px' }}>
        <label style={{ display: 'grid', gap: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>Name</span>
          <input
            className="input"
            data-testid={`${testId}-name`}
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            required
            value={name}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>Role</span>
          <input
            className="input"
            data-testid={`${testId}-role`}
            disabled={saving}
            onChange={(e) => setRole(e.target.value)}
            /*
             * NO PLACEHOLDER. Stacie, 2026-09-03: "I don't actually like that
             * it defaults the role. See how it's Director of Sales in gray?
             * That almost looks like I don't need to fill it in."
             *
             * She is describing a real failure of grey placeholder text — it
             * renders at the same weight as a filled value, so the field reads
             * as answered. The label already says Role; an example inside the
             * box buys nothing and costs a wrong record.
             */
            value={role}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>Email</span>
          <input
            className="input"
            data-testid={`${testId}-email`}
            disabled={saving}
            onChange={(e) => setEmail(e.target.value)}
            // Deliberately NOT type="email": the browser's own validation would
            // block submit before our message could name the problem, and it
            // disagrees with the server's rule at the edges.
            type="text"
            value={email}
          />
        </label>
        <label style={{ display: 'grid', gap: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>Phone</span>
          <input
            className="input"
            data-testid={`${testId}-phone`}
            disabled={saving}
            onChange={(e) => setPhone(e.target.value)}
            value={phone}
          />
        </label>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          checked={isPrimary}
          data-testid={`${testId}-primary`}
          disabled={saving || left}
          onChange={(e) => setIsPrimary(e.target.checked)}
          type="checkbox"
        />
        <span style={{ fontSize: 12 }}>
          {copy.primaryLabel}
          {/* Disabled rather than hidden when they have left, so the rule is
              visible instead of being discovered as a server error. */}
          {left && <span className="muted"> {copy.departedPrimaryHint}</span>}
        </span>
      </label>

      {editing && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            checked={left}
            data-testid={`${testId}-left`}
            disabled={saving}
            onChange={(e) => {
              setLeft(e.target.checked);
              // Marking someone departed clears the primary box, matching what the
              // server does anyway. Leaving both ticked would show a state that
              // cannot be saved.
              if (e.target.checked) setIsPrimary(false);
            }}
            type="checkbox"
          />
          <span style={{ fontSize: 12 }}>{copy.departedLabel}</span>
        </label>
      )}

      {error && (
        <p
          className="detail-note"
          data-testid={`${testId}-error`}
          style={{ margin: 0 }}
        >
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-primary btn-sm"
          data-testid={`${testId}-save`}
          disabled={saving || name.trim() === ''}
          type="submit"
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add contact'}
        </button>
        <button
          className="btn btn-sm"
          data-testid={`${testId}-cancel`}
          disabled={saving}
          onClick={onCancel}
          type="button"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
